import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { User } from 'oidc-client-ts'
import { getAuth } from '@/auth/oidc'

/* Session state derived from the OIDC user held by oidc-client-ts (which owns
   token storage + silent renewal). This store is a thin reactive view plus the
   redirect actions the router and layout call. */
export const useSessionStore = defineStore('session', () => {
  const user = ref<User | null>(null)

  const isAuthenticated = computed(() => !!user.value && !user.value.expired)
  const token = computed(() => (user.value && !user.value.expired ? user.value.access_token : null))
  /* Identity is only meaningful while the session is valid - stay consistent
     with token/isAuthenticated and return nothing once the user is expired. */
  const displayName = computed(() => {
    if (!user.value || user.value.expired) return ''
    const p = user.value.profile
    return (p?.name as string | undefined) ?? (p?.email as string | undefined) ?? ''
  })
  const email = computed(() =>
    user.value && !user.value.expired
      ? ((user.value.profile.email as string | undefined) ?? '')
      : '',
  )

  /* Load any persisted user at bootstrap (expired ones are treated as null). */
  async function init(): Promise<void> {
    try {
      const u = await getAuth().getUser()
      user.value = u && !u.expired ? u : null
    } catch {
      user.value = null
    }
  }

  /* Begin login: redirect to the IdP, remembering where to return. */
  async function login(redirect = '/'): Promise<void> {
    await getAuth().signinRedirect({ state: { redirect } })
  }

  /* Finish login on the /auth/callback route; returns the target path. */
  async function completeLogin(): Promise<string> {
    const u = await getAuth().signinRedirectCallback()
    user.value = u
    const st = u.state as { redirect?: string } | undefined
    // Only honor same-origin path redirects to prevent open-redirect abuse.
    // Reject "//host" (protocol-relative) which also starts with a slash.
    const redirect = st?.redirect
    return redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/'
  }

  /* Full logout: end the IdP session too (best effort - local state is cleared
     regardless so the user is never stuck logged-in locally). */
  async function logout(): Promise<void> {
    user.value = null
    try {
      await getAuth().signoutRedirect()
    } catch (err) {
      console.warn('signout failed', err)
      try {
        await getAuth().removeUser()
      } catch {
        /* ignore */
      }
    }
  }

  /* Local invalidation (e.g. an Unauthenticated RPC): drop the stored user. */
  function invalidate(): void {
    user.value = null
    void getAuth()
      .removeUser()
      .catch(() => {})
  }

  /* Kept in sync with oidc-client-ts events (renew / external logout). */
  function setUser(u: User | null): void {
    user.value = u
  }

  return {
    user,
    isAuthenticated,
    token,
    displayName,
    email,
    init,
    login,
    completeLogin,
    logout,
    invalidate,
    setUser,
  }
})
